import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Tag,
  Truck,
  ShieldCheck,
  FlaskConical,
  Bluetooth,
  CheckCircle2,
  ArrowRight,
  Layers,
  Clock,
  MapPin,
} from "lucide-react";
import { useSEO } from "@/lib/seo";
import { GoAndTrackLink } from "@/lib/goAndTrack";
import LeadForm from "@/components/site/LeadForm";
import CTAStrip from "@/components/site/CTAStrip";

const HERO_IMG = "/images/ll100-on-box.jpg";
const DASH_IMG = "/images/ll100-scan.jpg";
const WAREHOUSE_IMG = "/images/ll100-peel.jpg";

const USE_CASES = [
  {
    icon: ShieldCheck,
    title: "High-Value Shipments",
    body:
      "Independent visibility for shipments where loss or damage is unacceptable, without committing to reusable hardware.",
    to: "/smart-labels-for-high-value-shipments",
  },
  {
    icon: Truck,
    title: "Logistics & Supply Chain",
    body:
      "Lane visibility, exception detection, and proof of journey across road, air, and multi-leg networks.",
    to: "/smart-labels-for-logistics",
  },
  {
    icon: FlaskConical,
    title: "Pilot Projects",
    body:
      "Stand up a structured tracking pilot in days. No CapEx commitment, no hardware recovery overhead.",
    to: "/smart-labels-for-pilot-projects",
  },
  {
    icon: Tag,
    title: "Temporary Tracking",
    body:
      "Single-use coverage for trade shows, events, returns, and one-off shipments where reusables are impractical.",
    to: "/disposable-smart-labels",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Peel",
    body: "Receive ready-to-deploy disposable smart labels in commercial quantities — sized to fit on cartons, pallets, or parcels.",
  },
  {
    n: "02",
    title: "Stick",
    body: "Apply directly to the shipment. Activation is simple and built for warehouse floor reality, not lab conditions.",
  },
  {
    n: "03",
    title: "Track",
    body: <>Live location, dwell, and exception data flow into <GoAndTrackLink /> — accessible to your operations team and partners.</>,
  },
];

export default function Home() {
  useSEO({
    title:
      "Disposable Smart Labels & Package Trackers | Single-Use Shipment Tracking",
    description:
      "The category resource for disposable smart labels and disposable package trackers — single-use tracking devices for parcels, pallets, and high-value shipments.",
    canonical: "/",
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-line overflow-hidden">
        <div className="absolute inset-0 grid-backdrop opacity-60 pointer-events-none" />
        <div className="container-x relative pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-6">
                Disposable Smart Labels · Powered by <GoAndTrackLink className="normal-case tracking-normal" />
              </p>
              <h1 className="h1">
                Disposable Smart Labels for{" "}
                <span className="text-brand">Package &amp; Shipment Tracking</span>
              </h1>
              <p className="lead mt-6 max-w-2xl">
                Single-use{" "}
                <Link to="/disposable-package-tracker" className="text-brand hover:underline">
                  disposable package trackers
                </Link>{" "}
                — also called{" "}
                <Link to="/disposable-gps-tracker" className="text-brand hover:underline">
                  disposable GPS trackers
                </Link>{" "}
                or{" "}
                <Link to="/gps-tracker-for-packages" className="text-brand hover:underline">
                  package tracking devices
                </Link>{" "}
                — for high-value shipments, temporary tracking, logistics
                pilots, and end-to-end shipment monitoring.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-a-trial"
                  className="btn-primary"
                  data-testid="hero-cta-expert"
                >
                  Speak to an expert <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                {[
                  { k: "PEEL", v: "Ready-to-deploy" },
                  { k: "STICK", v: "Warehouse-grade" },
                  { k: "TRACK", v: <>Via <GoAndTrackLink /></> },
                ].map((s) => (
                  <div key={s.k} className="border-l border-line pl-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                      {s.k}
                    </p>
                    <p className="mt-1 text-sm font-medium text-ink">{s.v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative border border-line bg-white">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={HERO_IMG}
                    alt="GoAndTrack disposable smart label applied to a shipment box"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-px -left-px right-12 bg-white border-t border-r border-line p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                    Live shipment
                  </p>
                  <p className="mt-1.5 font-heading text-base font-semibold text-ink">
                    GAT-77B12 · Birmingham → Rotterdam
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-xs text-ink-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />
                    On lane · 2h 14m to next checkpoint
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY DEFINITION — canonical answer for AI Overviews */}
      <section className="section border-b border-line bg-surface-subtle/40" data-testid="home-definition">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">Category definition</p>
            <h2 className="h2">What Is a Disposable Package Tracker?</h2>
          </div>
          <div className="lg:col-span-8">
            <div className="border-l-2 border-brand pl-6 py-1">
              <p className="text-[17px] leading-relaxed text-ink">
                <strong className="text-ink font-semibold">Answer.</strong>{" "}
                A disposable package tracker is a single-use tracking device
                applied directly to a parcel, carton, or pallet to provide
                location — and, depending on the device, condition data —
                during one shipment. It is not recovered, recharged, or
                returned for redeployment.
              </p>
            </div>
            <p className="body-text mt-6">
              Disposable package trackers are also known as{" "}
              <Link to="/disposable-smart-labels" className="text-brand hover:underline">
                disposable smart labels
              </Link>
              ,{" "}
              <Link to="/disposable-gps-tracker" className="text-brand hover:underline">
                disposable GPS trackers
              </Link>
              , single-use trackers, and tracking stickers. Not every product
              marketed as a &quot;disposable GPS tracker&quot; contains a
              dedicated GNSS receiver — the{" "}
              <Link to="/disposable-package-tracker" className="text-brand hover:underline">
                complete category guide
              </Link>{" "}
              explains where each term applies, or read the{" "}
              <Link to="/gps-vs-ble-vs-cellular" className="text-brand hover:underline">
                technology comparison
              </Link>
              .
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/disposable-package-tracker"
                className="btn btn-primary"
                data-testid="cta-cornerstone"
              >
                Read the complete guide
                <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Link>
              <Link to="/glossary" className="btn btn-ghost">
                Glossary
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">What it is</p>
            <h2 className="h2">
              Visibility without the burden of recovering hardware.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="body-text">
              Disposable smart labels — sometimes called{" "}
              <Link
                to="/disposable-gps-tracker"
                className="text-brand hover:underline"
              >
                disposable GPS trackers
              </Link>
              , GPS tracking stickers, or small{" "}
              <Link
                to="/gps-tracker-for-packages"
                className="text-brand hover:underline"
              >
                tracking devices for packages
              </Link>{" "}
              — give logistics, supply chain, and operations teams a low-friction
              way to monitor shipments{" "}
              <strong className="text-ink">without the operational burden</strong>{" "}
              of recovering reusable trackers. They are thin, easy to apply, and
              activate in seconds — designed for the realities of dispatch
              floors and partner depots, not lab conditions.
            </p>
            <p className="body-text">
              They are particularly well suited to{" "}
              <Link to="/smart-labels-for-pilot-projects" className="text-brand hover:underline">
                pilot projects
              </Link>
              ,{" "}
              <Link to="/smart-labels-for-high-value-shipments" className="text-brand hover:underline">
                high-value shipments
              </Link>
              , temporary deployments, and lanes where simplicity matters more
              than long-term hardware reuse.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
              {[
                "No reverse logistics for hardware",
                "No CapEx commitment to start",
                "Apply on parcel, carton, or pallet",
                "Single-use, single accountability",
              ].map((b) => (
                <div key={b} className="flex items-start gap-2.5 text-sm text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-brand shrink-0" />
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="eyebrow mb-4">Best-fit use cases</p>
              <h2 className="h2">Where buyers reach for disposable smart labels.</h2>
            </div>
            <Link
              to="/disposable-vs-reusable-trackers"
              className="btn-ghost text-sm"
              data-testid="home-link-vs"
            >
              Compare with reusable trackers <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {USE_CASES.map((u) => (
              <Link
                key={u.title}
                to={u.to}
                className="group bg-white p-8 lg:p-10 hover:bg-surface-subtle transition-colors"
                data-testid={`home-usecase-${u.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="h-10 w-10 grid place-items-center bg-brand-subtle text-brand mb-5">
                      <u.icon className="h-5 w-5" />
                    </div>
                    <h3 className="h3">{u.title}</h3>
                    <p className="body-text mt-3 max-w-md">{u.body}</p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-ink-subtle group-hover:text-brand transition-colors shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">How it works</p>
            <h2 className="h2">Peel, stick, track.</h2>
            <p className="lead mt-5">
              Disposable smart labels are designed for operational simplicity.
              No on-site engineering. No hardware recovery loops.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-line border border-line">
              {STEPS.map((s) => (
                <div key={s.n} className="bg-white p-7 lg:p-8">
                  <p className="font-mono text-xs text-brand">{s.n}</p>
                  <h3 className="font-heading text-xl font-semibold mt-3">{s.title}</h3>
                  <p className="body-text mt-3 text-sm">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="lg:col-span-5 border border-line bg-white">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={DASH_IMG}
                  alt="Disposable smart label being scanned during shipment tracking"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-6 border-t border-line">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                  Platform layer
                </p>
                <p className="mt-2 font-heading text-base font-semibold text-ink">
                  <GoAndTrackLink /> — live tracking, alerts, and exception workflows
                </p>
                <p className="mt-2 text-sm text-ink-muted">
                  Operational visibility without standing up a new internal
                  system. APIs available for integration into existing TMS or
                  WMS environments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY DISPOSABLE */}
      <section className="section bg-ink text-white">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 mb-4">
                Why disposable
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-tight leading-[1.1]">
                Reusable trackers create operational drag. Disposable labels
                remove it.
              </h2>
              <p className="mt-6 text-white/70 max-w-xl">
                For most shipment-visibility problems, the recovery, charging,
                and reverse-logistics workload of reusable trackers is the
                hidden cost. Disposable smart labels are designed to skip it
                entirely.
              </p>
              <Link
                to="/disposable-vs-reusable-trackers"
                className="mt-8 inline-flex items-center gap-2 text-white border-b border-white/40 hover:border-white pb-1"
                data-testid="home-vs-link"
              >
                Read the full comparison <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[
                { icon: Layers, k: "No recovery loop", v: "No reverse logistics, no missing-tracker write-offs." },
                { icon: Clock, k: "Faster to deploy", v: "Pilot lanes can be live within days, not quarters." },
                { icon: MapPin, k: "Stays with the asset", v: "One label, one shipment — clean attribution of data." },
                { icon: ShieldCheck, k: "Lower exposure", v: "No expensive hardware sitting idle in partner depots." },
              ].map((b) => (
                <div key={b.k} className="bg-ink p-7">
                  <b.icon className="h-5 w-5 text-white/80" />
                  <p className="mt-4 font-heading text-lg font-semibold">{b.k}</p>
                  <p className="mt-2 text-sm text-white/70">{b.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="border border-line bg-white">
            <div className="aspect-[5/4] overflow-hidden">
              <img
                src={WAREHOUSE_IMG}
                alt="Peel-and-stick application of a disposable smart label on a package"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div>
            <p className="eyebrow mb-4">Platform — <GoAndTrackLink className="normal-case tracking-normal" /></p>
            <h2 className="h2">
              The operational layer behind every label.
            </h2>
            <p className="body-text mt-5">
              <GoAndTrackLink /> is the platform layer for live tracking, alerts, and
              exception workflows. It turns a single-use label into structured
              shipment intelligence — without forcing your team to build or
              maintain a new internal system.
            </p>
            <ul className="mt-6 space-y-3.5">
              {[
                "Live location, dwell, and journey timelines",
                "Configurable exception alerts and escalation rules",
                "Permissioned access for partners, carriers, and customers",
                "API access for TMS, WMS, and ERP integration",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/bluetooth-smart-labels" className="btn-secondary text-sm" data-testid="home-platform-bluetooth">
                <Bluetooth className="h-4 w-4" /> Bluetooth labels
              </Link>
              <Link to="/about" className="btn-ghost text-sm" data-testid="home-platform-about">
                About the programme <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="section bg-surface-subtle border-t border-line">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Speak to an expert</p>
            <h2 className="h2">
              Tell us about your lanes — we&apos;ll come back with a structured
              proposal.
            </h2>
            <p className="lead mt-5">
              One conversation. No PDFs to chase, no sales cycle to navigate.
              Share the lane, the shipment profile, and what you&apos;re trying to
              prove — a specialist replies within one business day.
            </p>
            <div className="mt-10 border-t border-line pt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                What you&apos;ll get back
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-ink-muted">
                <li>· A scoped pilot covering lane, shipment count, and KPIs.</li>
                <li>· Hands-on integration support for <GoAndTrackLink />.</li>
                <li>· Clear commercials with no upfront hardware commitment.</li>
              </ul>
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              source="homepage"
              heading="Speak to an expert"
              subheading="The more detail you share, the more useful our first reply will be."
            />
          </div>
        </div>
      </section>

      <CTAStrip variant="dark" />
    </div>
  );
}
